import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Calendar, Cpu, Database, FileText, ListChecks, Pencil, Upload, Users } from "lucide-react";
import { useProjectDocuments } from "@lumanity/ui/evidence";
import {
  ProjectAccessDenied,
  ProjectCustomFields,
  formatStartDate,
  isProjectMember,
  useCustomFields,
  useProject,
  useSaveProject,
  type Project,
} from "@lumanity/ui/projects";
import { Button, ErrorBanner, toast } from "@lumanity/ui/primitives";
import PageLayout from "@/components/PageLayout";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import WizardStepper, { type WizardStep } from "@/components/WizardStepper";
import {
  STEP_IDS,
  STEP_LABELS,
  completedSteps,
  isStepId,
  normaliseStepParam,
  reachableSteps,
  startingStep,
  statusAfter,
  type ProjectProgress,
  type StepId,
} from "@/config/projectStatus";
import { ChatLauncher } from "@/features/chat";
import { ProjectEvidenceBase, ProjectUpload } from "@/features/evidence";
import { ProjectOutputs, useProjectOutputs } from "@/features/outputs";
import ProjectDetailsPanel from "@/features/project/ProjectDetailsPanel";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { modelLabel } from "@/lib/models";

const STEP_ICONS = { fields: ListChecks, upload: Upload, evidence: Database, outputs: FileText } as const;

const ContinueButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <Button type="button" variant="brand" size="sm" className="gap-1" onClick={onClick} disabled={disabled}>
    Continue <ArrowRight className="h-4 w-4" />
  </Button>
);

const ProjectHeader = ({ project, canEditSetup }: { project: Project; canEditSetup: boolean }) => (
  <div className="flex items-start justify-between gap-4 mb-4">
    <div>
      <h1 className="text-2xl font-bold text-brand-navy">{project.name}</h1>
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1">
        {project.code && <span className="font-medium text-brand-navy">{project.code}</span>}
        {project.client_name && <span>{project.client_name}</span>}
        {project.start_date && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Started {formatStartDate(project.start_date)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" /> {project.member_count} team {project.member_count === 1 ? "member" : "members"}
        </span>
        {modelLabel(project.properties?.model) && (
          <span className="flex items-center gap-1">
            <Cpu className="w-3.5 h-3.5" /> {modelLabel(project.properties?.model)}
          </span>
        )}
      </div>
    </div>
    {canEditSetup && (
      <Button asChild variant="outline" size="sm">
        <Link to={`/projects/${project.id}/edit`}>
          <Pencil className="w-4 h-4 mr-1" /> Edit project setup
        </Link>
      </Button>
    )}
  </div>
);

/**
 * A project's guided flow, like FRaiME's: Custom fields → Upload files →
 * Evidence Base → Outputs. Opens at the furthest step reached (the
 * project's status in the project service, plus what it has); earlier steps
 * can be revisited, later ones open through Continue. The step is in `?step=`.
 * Evidence Chat is a floating assistant on every step once there are documents.
 */
const ProjectWorkspace = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: project, isLoading, error } = useProject(id);
  const { data: customFields, isLoading: fieldsLoading } = useCustomFields(id);
  const { data: projectDocs, isLoading: docsLoading } = useProjectDocuments(id);
  const { data: outputs, isLoading: outputsLoading } = useProjectOutputs(id);
  const saveProject = useSaveProject();

  const [uploadDirty, setUploadDirty] = useState(false);
  const { isBlocked, confirmLeave, cancelLeave, guardedNavigate } = useUnsavedChangesGuard(uploadDirty);
  const [furthestVisited, setFurthestVisited] = useState<StepId>("fields");

  const ready = !!project && !fieldsLoading && !docsLoading && !outputsLoading;
  const progress: ProjectProgress = {
    status: project?.status,
    hasCustomFields: (customFields?.length ?? 0) > 0,
    hasDocuments: (projectDocs?.documentIds.length ?? 0) > 0,
    hasOutputs: (outputs?.length ?? 0) > 0,
  };
  const requested = normaliseStepParam(searchParams.get("step"));
  const reachable = reachableSteps(progress, furthestVisited);
  // A step from the URL only if it's reachable; otherwise the furthest step reached.
  const step: StepId = isStepId(requested) && reachable.includes(requested) ? requested : startingStep(progress);

  useEffect(() => {
    if (!ready) return;
    setFurthestVisited((prev) => (STEP_IDS.indexOf(step) > STEP_IDS.indexOf(prev) ? step : prev));
  }, [ready, step]);

  const steps: WizardStep[] = useMemo(
    () =>
      STEP_IDS.map((stepId) => ({
        id: stepId,
        label: STEP_LABELS[stepId],
        icon: STEP_ICONS[stepId],
        disabled: !reachable.includes(stepId),
      })),
    [reachable],
  );

  const goTo = (next: StepId) => {
    setFurthestVisited((prev) => (STEP_IDS.indexOf(next) > STEP_IDS.indexOf(prev) ? next : prev));
    setSearchParams({ step: next }, { replace: true });
  };

  /** Record that the project moved past `from` (FRaiME's status values); never blocks the move. */
  const advanceStatus = (from: StepId) => {
    const next = project ? statusAfter(from, project.status) : null;
    if (!project || !next) return;
    saveProject.mutate({ projectId: project.id, changes: { status: next, version: project.version }, members: null }, { onError: () => undefined });
  };

  if (error) {
    // The service answers 404 for projects the user can't see.
    return (
      <PageLayout>
        {/not found|404|403/i.test(error.message) ? (
          <ProjectAccessDenied onBack={() => navigate("/")} backLabel="Back to Home" />
        ) : (
          <ErrorBanner message={`Could not load the project: ${error.message}`} />
        )}
      </PageLayout>
    );
  }
  if (isLoading || !project || !ready) {
    return (
      <PageLayout>
        <div className="text-muted-foreground py-12 text-center">Loading project...</div>
      </PageLayout>
    );
  }
  if (!isProjectMember(project)) {
    // App admins can list every project; they open its setup to add themselves first.
    return (
      <PageLayout>
        <ProjectAccessDenied canEditSetup onBack={() => navigate("/")} onEditSetup={() => navigate(`/projects/${project.id}/edit`)} backLabel="Back to Home" />
      </PageLayout>
    );
  }

  const canEditSetup = project.my_role === "owner" || project.my_role === "editor";
  const canEdit = project.my_role !== "viewer";

  return (
    <PageLayout>
      <ProjectHeader project={project} canEditSetup={canEditSetup} />
      <ProjectDetailsPanel projectId={project.id} canEditSetup={canEditSetup} />

      <WizardStepper
        steps={steps}
        activeIndex={STEP_IDS.indexOf(step)}
        completedIndices={completedSteps(progress).map((s) => STEP_IDS.indexOf(s))}
        onStepClick={(index) => guardedNavigate(() => goTo(STEP_IDS[index]))}
      />
      <UnsavedChangesDialog open={isBlocked} onConfirm={confirmLeave} onCancel={cancelLeave} />

      {step === "fields" && (
        <div className="space-y-4">
          <ProjectCustomFields
            projectId={project.id}
            disabled={!canEditSetup}
            onError={(e) => toast({ title: "Custom fields", description: e.message, variant: "destructive" })}
          />
          <div className="flex justify-end">
            <ContinueButton onClick={() => goTo("upload")} />
          </div>
        </div>
      )}

      {step === "upload" &&
        (canEdit ? (
          <ProjectUpload
            projectId={project.id}
            onDirtyChange={setUploadDirty}
            onCancel={progress.hasDocuments ? () => goTo("evidence") : undefined}
            onComplete={() => {
              setUploadDirty(false);
              advanceStatus("upload");
              goTo("evidence");
            }}
          />
        ) : (
          <div className="rounded-lg border bg-card py-10 text-center text-muted-foreground">Viewers can't upload documents.</div>
        ))}

      {step === "evidence" && (
        <ProjectEvidenceBase
          projectId={project.id}
          onUpload={canEdit ? () => goTo("upload") : undefined}
          toolbarActions={
            <ContinueButton
              disabled={!progress.hasDocuments}
              onClick={() => {
                if (canEdit) advanceStatus("evidence");
                goTo("outputs");
              }}
            />
          }
        />
      )}

      {step === "outputs" && <ProjectOutputs projectId={project.id} kantataProjectId={project.kantata_project_id} />}

      {progress.hasDocuments && <ChatLauncher projectId={project.id} />}
    </PageLayout>
  );
};

export default ProjectWorkspace;
